// Live alerts for the map — a new attention item pops up top-right, three at a
// time (W6's spec: upper right, stack of three, deep-link to what it concerns).
// Which three, and in what order, is `planAlertStack` (BL-19); SOS is not here
// at all — it has its own lane, exempt from the limit.
//
// ⚠ WHAT IS ALREADY THERE WHEN THE PAGE OPENS IS NOT NEWS. The first render
// seeds the "seen" set silently; otherwise opening the map on a busy evening
// fires a dozen toasts at once, and a wall of alerts reads as none. The
// Attention tab is where the standing list lives.

import { useEffect, useRef } from "react";
import { toast } from "sonner";
import type { LiveAttentionItem } from "./live-map.attention";
import {
  clearUrgentShowing,
  HEADS_UP_ALERT_MS,
  isUrgent,
  noteUrgentGone,
  noteUrgentShown,
  planAlertStack,
  URGENT_ALERT_MS,
  urgentShowing,
} from "./live-map.alerts";

export const LIVE_MAP_TOASTER_ID = "live-map";

export function useLiveMapAlerts(
  items: readonly LiveAttentionItem[] | null,
  onOpen: (item: LiveAttentionItem) => void,
) {
  const seen = useRef<Set<string> | null>(null);
  const openRef = useRef(onOpen);
  openRef.current = onOpen;

  // What a new heads-up must not bury is counted per TOASTER, not per hook:
  // the bell's notifications land in this same stack (live-map.alerts).
  useEffect(() => () => clearUrgentShowing(LIVE_MAP_TOASTER_ID), []);

  useEffect(() => {
    // `null` = the reads behind the list have not all landed; seeding from a
    // half-loaded list would announce the rest as new a second later.
    if (items === null) return;
    if (seen.current === null) {
      seen.current = new Set(items.map((item) => item.id));
      return;
    }
    const fresh = items.filter((item) => !seen.current?.has(item.id));
    // Marked seen whether or not it is shown: a heads-up dropped because the
    // stack was full is in the Attention tab, and must not pop up later as if new.
    for (const item of fresh) seen.current.add(item.id);

    for (const item of planAlertStack(fresh, urgentShowing(LIVE_MAP_TOASTER_ID))) {
      const urgent = isUrgent(item);
      const toastId = `live-alert-${item.id}`;
      const settle = () => noteUrgentGone(LIVE_MAP_TOASTER_ID, toastId);
      if (urgent) noteUrgentShown(LIVE_MAP_TOASTER_ID, toastId);
      (urgent ? toast.error : toast.warning)(item.title, {
        id: toastId,
        toasterId: LIVE_MAP_TOASTER_ID,
        description: item.detail,
        duration: urgent ? URGENT_ALERT_MS : HEADS_UP_ALERT_MS,
        action: { label: "Open", onClick: () => openRef.current(item) },
        onDismiss: settle,
        onAutoClose: settle,
      });
    }
  }, [items]);
}
